import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { TASK_BOARD_DONE_MAX, type TaskCard, type TaskDetail, type TaskNotification, type TaskTagCount } from '@shared/types/tasks';
import { usePermission } from '@/hooks/usePermission';
import { getTask, getTaskNotifications } from '@/lib/tasks-api';
import { boardKey, cardInScope, useTaskStore, type BoardQuery } from '@/stores/taskStore';
import { applyToBoard, applyToDetail, useTaskOptimistic } from '@/stores/taskOptimistic';

const LIVE_DEBOUNCE_MS = 250;

/** Refetch this task everywhere it's shown (works even when the live socket is down). */
export function touchTask(taskId: string) {
  useTaskStore.getState().receive({ type: 'task', task_id: taskId, actor_user_id: null });
}

/** Board (or a Tasks panel's scoped board), refreshed whenever the live socket reports a change (one task's: just its card). */
export function useTaskBoard(query: BoardQuery, enabled = true) {
  const key = boardKey(query);
  const entry = useTaskStore((s) => s.boards[key]);
  const optimistic = useTaskOptimistic();
  const boardTick = useTaskStore((s) => s.boardTick);
  const loadBoard = useTaskStore((s) => s.loadBoard);
  // The latest query for `reload`, which callers may hold on to between renders.
  const queryRef = useRef(query);
  useLayoutEffect(() => {
    queryRef.current = query;
  });

  const reload = useCallback(() => loadBoard(queryRef.current), [loadBoard]);
  const loadOlderDone = useTaskStore((s) => s.loadOlderDone);
  const searchFinished = useTaskStore((s) => s.searchFinished);
  /** Show older: the next finished tasks into this board. */
  const loadOlder = useCallback(() => loadOlderDone(queryRef.current), [loadOlderDone]);
  /** The board's search also finds older finished tasks it hasn't loaded. */
  const searchOlder = useCallback((q: string) => searchFinished(queryRef.current, q), [searchFinished]);

  useEffect(() => {
    if (enabled) void reload();
  }, [key, enabled, reload]);

  const firstTick = useRef(boardTick);
  useEffect(() => {
    if (!enabled || boardTick === firstTick.current) return undefined;
    const timer = setTimeout(() => void reload(), LIVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [boardTick, enabled, reload]);

  // Pending changes and just-created tasks show on top of what was fetched.
  const board = useMemo(() => {
    if (!entry?.data) return null;
    const { scope, archived } = entry.query;
    return applyToBoard(
      entry.data,
      entry.fetchedFrom,
      optimistic,
      (card) => cardInScope(card, scope) && (archived || !card.archived),
      entry.cardsFetchedFrom
    );
  }, [entry, optimistic]);

  return {
    key,
    board,
    loading: entry?.loading ?? enabled,
    error: entry?.error ?? null,
    reload,
    /** Finished tasks not loaded yet (the Board only; absent for a Tasks panel's board, and once Show older is at its limit). */
    olderDone: entry?.data?.older_done && entry.doneLimit < TASK_BOARD_DONE_MAX ? entry.data.older_done : null,
    loadingOlder: entry?.loadingOlder ?? false,
    loadOlder,
    /** The search for older finished tasks failed (Retry sends it again). */
    searchError: entry?.search.error ?? null,
    /** The search found more older tasks than it sent (only the latest show). */
    searchMore: entry?.search.more ?? false,
    /** Changes with each full fetch of this board, after which its search is sent again. */
    fetchedFrom: entry?.fetchedFrom ?? 0,
    searchOlder,
  };
}

function seedDetail(card: TaskCard, seed: Partial<TaskDetail> = {}): TaskDetail {
  // Until it loads, the version is when the task was made (its description's version before any save).
  return {
    ...card,
    description: '',
    description_updated_at: card.created_at,
    following: true,
    events: [],
    attachments: [],
    ...seed,
  };
}

/**
 * One task with its timeline; refetches when that task changes anywhere. Until the server
 * answers it shows the card already on the Board (or the task being created), so opening a
 * task is instant; `hydrated` says whether the description and timeline have loaded.
 */
export function useTaskDetail(taskId: string | null) {
  // Both remember which task they're for, so switching tasks needs no reset: a result for
  // another task is simply ignored.
  const [server, setServer] = useState<{ task: TaskDetail; fetchedFrom: number } | null>(null);
  const [failure, setFailure] = useState<{ taskId: string; message: string; status?: number } | null>(null);
  const tick = useTaskStore((s) => (taskId ? s.taskTicks[taskId] ?? 0 : 0));
  const boards = useTaskStore((s) => s.boards);
  const optimistic = useTaskOptimistic();
  const latest = useRef(taskId);
  useLayoutEffect(() => {
    latest.current = taskId;
  });

  // State is only set once the request answers (and only if that task is still the one shown).
  const load = useCallback((id: string) => {
    const startedAt = Date.now();
    // True when the task arrived (callers like Reload keep their draft otherwise).
    return getTask(id).then((res) => {
      if (latest.current !== id) return false;
      if (res.data) {
        setServer({ task: res.data.task, fetchedFrom: startedAt });
        setFailure(null);
        return true;
      }
      setFailure({ taskId: id, message: res.error ?? 'Could not load the task', status: res.status });
      return false;
    });
  }, []);
  const reload = useCallback(async () => (taskId ? load(taskId) : false), [taskId, load]);

  useEffect(() => {
    if (taskId) void load(taskId);
  }, [taskId, load]);

  const firstTick = useRef(tick);
  useEffect(() => {
    if (!taskId || tick === firstTick.current) return undefined;
    const timer = setTimeout(() => void load(taskId), LIVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [tick, taskId, load]);

  const seed = useMemo(() => {
    if (!taskId || (server && server.task.id === taskId)) return null;
    const created = optimistic.newTasks[taskId];
    if (created) return seedDetail(created.card, created.seed);
    for (const entry of Object.values(boards)) {
      const card = entry.data?.tasks.find((t) => t.id === taskId);
      if (card) return seedDetail(card);
    }
    return null;
  }, [taskId, server, boards, optimistic.newTasks]);

  const hydrated = Boolean(server && server.task.id === taskId);
  const task = useMemo(() => {
    const base = hydrated ? server!.task : seed;
    return base ? applyToDetail(base, hydrated ? server!.fetchedFrom : 0, optimistic) : null;
  }, [hydrated, server, seed, optimistic]);

  const failed = !task && failure?.taskId === taskId ? failure : null;
  // `notFound`: the server answered 404 (deleted, or a wrong link), not a failure to retry.
  return { task, hydrated, error: failed?.message ?? null, notFound: failed?.status === 404, reload };
}

export function useTaskNotifications(enabled: boolean) {
  const [notifications, setNotifications] = useState<TaskNotification[]>([]);
  const [unread, setUnread] = useState(0);
  const tick = useTaskStore((s) => s.notificationTick);

  const reload = useCallback(async () => {
    const res = await getTaskNotifications();
    if (res.data) {
      setNotifications(res.data.notifications);
      setUnread(res.data.unread);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    const timer = setTimeout(() => void reload(), tick === 0 ? 0 : LIVE_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [enabled, tick, reload]);

  return useMemo(() => ({ notifications, unread, reload, setNotifications, setUnread }), [notifications, unread, reload]);
}

const NO_TAGS: TaskTagCount[] = [];

/** The team's `#tags`, most used first, from the cached Board (the sidebar's count loads it anyway). */
export function useTeamTags(): TaskTagCount[] {
  const canView = usePermission('TASKS');
  const { board } = useTaskBoard({}, canView);
  return board?.tags ?? NO_TAGS;
}
